import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  WritableSignal,
  effect,
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OverlayScrollLockService } from '@core/services/overlay-scroll-lock.service';
import { HtmlSanitizeService } from '@core/utils/html-sanitize';

@Component({
  selector: 'app-popup',
  imports: [TranslateModule, NgClass],
  templateUrl: './popup.html',
  styleUrl: './popup.scss',
})
export class Popup implements AfterViewInit, OnDestroy {
  translate = inject(TranslateService);
  private scrollLock = inject(OverlayScrollLockService);
  private injector = inject(Injector);
  private htmlSanitize = inject(HtmlSanitizeService);
  private hasScrollLock = false;
  private overlayAttached = false;

  @ViewChild('overlay') overlayRef?: ElementRef<HTMLElement>;
  @Input() contentTxt = 'default content';
  private _contentHtml: SafeHtml | string = '';
  @Input() set contentHtml(value: SafeHtml | string) {
    if (value == null || value === '') {
      this._contentHtml = '';
      return;
    }
    this._contentHtml =
      typeof value === 'string' ? this.htmlSanitize.toSafeHtml(value) : value;
  }
  get contentHtml(): SafeHtml | string {
    return this._contentHtml;
  }
  @Input() btnTxt = 'default button';
  @Input() btnCancelTxt = 'default button';
  @Input() headerTxt = '';
  @Input() headerTxtClass = 'text-black';
  @Input() isAboveModal = false;
  @Input() isIcon = false;
  @Input() isAutoClose = true;
  @Input() isShowLoader = false;
  @Input() loader = false;
  @Input() imgSrc = '';
  @Input() imgClass = '';
  @Input({ required: true }) isPopupVisible!: WritableSignal<boolean>;
  @Output() btnClick = new EventEmitter<void>();
  @Output() cancelClick = new EventEmitter<void>();
  private visibilityEffect = effect(() => {
    if (!this.isPopupVisible) {
      return;
    }

    if (this.isPopupVisible()) {
      this.lockBackgroundScroll();
    } else {
      this.unlockBackgroundScroll();
    }
  }, { injector: this.injector });

  get translatedContent() {
    if(this.contentTxt){
      return this.translate.instant(this.contentTxt) || this.contentTxt;
    }
    // If the string exists in translations, return the translated one
  }

  get translatedBtn() {
    return this.translate.instant(this.btnTxt) || this.btnTxt;
  }

  get translatedCancelBtn() {
    return this.translate.instant(this.btnCancelTxt) || this.btnCancelTxt;
  }

  get translatedHeader() {
    return this.translate.instant(this.headerTxt) || this.headerTxt;
  }

  ngAfterViewInit(): void {
    this.attachOverlayToBody();
  }

  ngOnDestroy(): void {
    this.visibilityEffect.destroy();
    this.detachOverlay();
    this.unlockBackgroundScroll();
  }

  /** Avoid fixed overlays being clipped by transformed ancestors (e.g. mobile sidebar). */
  private attachOverlayToBody(): void {
    const el = this.overlayRef?.nativeElement;
    if (!el || this.overlayAttached || typeof document === 'undefined') {
      return;
    }
    if (el.parentElement !== document.body) {
      document.body.appendChild(el);
    }
    this.overlayAttached = true;
  }

  private detachOverlay(): void {
    this.overlayRef?.nativeElement?.remove();
    this.overlayAttached = false;
  }

  closePopup() {
    this.btnClick.emit();
    if(this.isAutoClose){
      this.isPopupVisible.set(false);
    }
  }
  cancelAction(){
    this.cancelClick.emit();
    this.isPopupVisible.set(false);
  }

  private lockBackgroundScroll(): void {
    if (this.hasScrollLock) {
      return;
    }

    this.hasScrollLock = true;
    this.scrollLock.lock();
  }

  private unlockBackgroundScroll(): void {
    if (!this.hasScrollLock) {
      return;
    }

    this.hasScrollLock = false;
    this.scrollLock.unlock();
  }
}
